// tape S02: persistent mixtape audio engine (singleton).
//
// The <audio> element + Web Audio graph live at module scope, NOT inside the
// stage widget, so playback keeps running when you close the tape and go poke
// at other commands — the widget is only a view bound to this state.
//
// Graph: MediaElementSource -> AnalyserNode (for the spectrum face) -> tapeGain
//        -> ctx.destination.  Deliberately NOT routed through the master gain,
//        so the global mute (which silences ambient drone/SFX) never kills a
//        track the visitor explicitly pressed play on. The tape has its own
//        volume slider. While a track plays, the 48 Hz drone is ducked to
//        silence and restored on pause/stop/album-end.

import { TRACKS } from './tracks'
import { getCtx, resumeOnGesture } from '../../audio/context'
import { setDroneMusicDuck } from '../../audio/drone'
import { load, set } from '../../core/save'

export interface TapeState {
  index: number // current track index (0-based)
  playing: boolean
  currentTime: number
  duration: number
  volume: number
  powered: boolean // has playback been started at least once this session
}

type Listener = (s: TapeState) => void

let audio: HTMLAudioElement | null = null
let analyser: AnalyserNode | null = null
let tapeGain: GainNode | null = null
let index = 0
let powered = false
let volume = 0.72
let lastPersist = 0

const listeners = new Set<Listener>()

// ---- persistence -----------------------------------------------------------

function restore(): void {
  const t = load().tapeState
  if (t) {
    index = Math.min(Math.max(t.index | 0, 0), TRACKS.length - 1)
    if (typeof t.volume === 'number') volume = Math.min(Math.max(t.volume, 0), 1)
  }
}
restore()

function persist(force = false): void {
  const t = Date.now()
  if (!force && t - lastPersist < 4000) return
  lastPersist = t
  set('tapeState', { index, pos: audio ? audio.currentTime : 0, volume })
}

// ---- state / listeners -----------------------------------------------------

export function getTapeState(): TapeState {
  return {
    index,
    playing: !!audio && !audio.paused && !audio.ended,
    currentTime: audio ? audio.currentTime : 0,
    duration: audio && isFinite(audio.duration) ? audio.duration : TRACKS[index].duration,
    volume,
    powered,
  }
}

export function onTapeChange(fn: Listener): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function emit(): void {
  const s = getTapeState()
  listeners.forEach((fn) => {
    try { fn(s) } catch { /* a view error must not break the engine */ }
  })
}

export function getAnalyser(): AnalyserNode | null {
  return analyser
}

// ---- graph -----------------------------------------------------------------

function ensureGraph(): void {
  if (audio) return
  audio = new Audio()
  audio.preload = 'metadata'

  audio.addEventListener('timeupdate', () => { persist(); emit() })
  audio.addEventListener('loadedmetadata', emit)
  audio.addEventListener('durationchange', emit)
  audio.addEventListener('play', () => { setDroneMusicDuck(true); emit() })
  audio.addEventListener('pause', () => { setDroneMusicDuck(false); persist(true); emit() })
  audio.addEventListener('ended', onEnded)

  // Web Audio tap for the visualizer + volume. Same-origin media → not tainted.
  try {
    const ctx = getCtx()
    tapeGain = ctx.createGain()
    tapeGain.gain.value = volume
    analyser = ctx.createAnalyser()
    analyser.fftSize = 64
    analyser.smoothingTimeConstant = 0.78
    const source = ctx.createMediaElementSource(audio)
    source.connect(analyser)
    analyser.connect(tapeGain)
    tapeGain.connect(ctx.destination)
    audio.volume = 1 // loudness is controlled by tapeGain when the graph exists
  } catch {
    analyser = null
    tapeGain = null
    audio.volume = volume
  }
}

function applyVolume(): void {
  if (tapeGain) tapeGain.gain.value = volume
  else if (audio) audio.volume = volume
}

function loadTrack(i: number, autoplay: boolean): void {
  ensureGraph()
  index = Math.min(Math.max(i, 0), TRACKS.length - 1)
  if (!audio) return
  const next = new URL(TRACKS[index].src, location.origin).href
  if (audio.src !== next) audio.src = TRACKS[index].src
  if (autoplay) {
    void resumeOnGesture().then(() => audio && audio.play().catch(() => {}))
  }
  persist(true)
  emit()
}

function onEnded(): void {
  if (index < TRACKS.length - 1) {
    loadTrack(index + 1, true)
  } else {
    // album over — release the drone, park at the last track
    setDroneMusicDuck(false)
    persist(true)
    emit()
  }
}

// ---- controls --------------------------------------------------------------

export function tapePlay(i?: number): void {
  ensureGraph()
  powered = true
  const target = typeof i === 'number' ? i : index
  if (typeof i === 'number' && i !== index) {
    loadTrack(target, true)
    return
  }
  if (!audio) return
  if (!audio.src) {
    // first play this session — restore saved position if it's the saved track
    const saved = load().tapeState
    loadTrack(index, false)
    if (saved && saved.index === index && saved.pos > 5 && saved.pos < TRACKS[index].duration - 5) {
      const seekTo = saved.pos
      audio.addEventListener('loadedmetadata', () => { if (audio) audio.currentTime = seekTo }, { once: true })
    }
  }
  void resumeOnGesture().then(() => audio && audio.play().catch(() => {}))
}

export function tapeToggle(): void {
  ensureGraph()
  if (!audio) return
  if (audio.paused || audio.ended) tapePlay()
  else audio.pause()
}

export function tapeNext(): void {
  const wasPlaying = !!audio && !audio.paused
  loadTrack(index + 1 >= TRACKS.length ? 0 : index + 1, wasPlaying || powered)
}

export function tapePrev(): void {
  // restart current track if we're >3s in, else go to previous
  if (audio && audio.currentTime > 3) {
    audio.currentTime = 0
    emit()
    return
  }
  const wasPlaying = !!audio && !audio.paused
  loadTrack(index - 1 < 0 ? TRACKS.length - 1 : index - 1, wasPlaying || powered)
}

export function tapeStop(): void {
  if (!audio) return
  audio.pause()
  audio.currentTime = 0
  setDroneMusicDuck(false)
  persist(true)
  emit()
}

export function tapeSeek(t: number): void {
  if (!audio) return
  const dur = isFinite(audio.duration) ? audio.duration : TRACKS[index].duration
  audio.currentTime = Math.min(Math.max(t, 0), dur)
  emit()
}

export function tapeSetVolume(v: number): void {
  volume = Math.min(Math.max(v, 0), 1)
  applyVolume()
  persist()
  emit()
}
